// Util/analytics.js
// ─────────────────────────────────────────────────────────────────────────────
// Usage analytics (backend: routes/analytics.js, Models1/analyticsModel.js).
// Records, per signed-in user:
//   • page views — route PATTERN (e.g. /party/details/:id, never ids), visible
//     time, load time (until the page's first API calls settle), API calls/errors
//   • events — export / print / search / filter / js_error (names only, never
//     field values or search text)
//   • the session — browser, OS, device type, screen size
// Everything is queued in memory and sent in small batches: on page change,
// every 15s, and when the tab is hidden or closed. Sent with fetch(keepalive)
// — not the axios client — so it can't trigger token-refresh loops, and a
// failed batch is simply dropped. Nothing here may ever break a page.
// ─────────────────────────────────────────────────────────────────────────────
import { getAccessToken } from "./authStorage.js";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const FLUSH_MS = 15000;
const LOAD_WINDOW_MS = 4000; // API calls started this soon after entering count as "page load"
const SESSION_KEY = "rj_analytics_session";

const uuid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });

const detectDevice = () => {
    const ua = navigator.userAgent || "";
    const browser = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Chrome\//.test(ua) ? "Chrome"
        : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Other";
    const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /(iPhone|iPad|iPod)/.test(ua) ? "iOS"
        : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "Other";
    const device_type = /iPad|Tablet/.test(ua) ? "tablet" : /Mobi|Android|iPhone/.test(ua) ? "mobile" : "desktop";
    return { user_agent: ua.slice(0, 300), browser, os, device_type };
};

const loadSession = () => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
        if (saved?.session_id) return saved;
    } catch { /* storage blocked */ }
    const s = {
        session_id: uuid(),
        started_at: new Date().toISOString(),
        ...detectDevice(),
        screen_w: window.screen?.width,
        screen_h: window.screen?.height,
        app_version: process.env.REACT_APP_VERSION || null,
    };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* ignore */ }
    return s;
};

let session = null;
let current = null;           // the page view being watched
const pendingViews = new Map(); // view_id → snapshot to send
let events = [];
let timer = null;
let started = false;

const now = () => Date.now();

const activeMs = (v) => v.activeMs + (v.visibleSince ? now() - v.visibleSince : 0);

const snapshot = (v) => ({
    view_id: v.view_id,
    route_pattern: v.route_pattern,
    component: v.component,
    entered_at: new Date(v.enteredAt).toISOString(),
    active_ms: Math.round(activeMs(v)),
    load_ms: v.loadMs,
    api_calls: v.apiCalls,
    api_errors: v.apiErrors,
});

/** Send what's queued (plus the current view's running totals). */
export const flush = () => {
    if (!started) return;
    const token = getAccessToken();
    if (current) pendingViews.set(current.view_id, snapshot(current));
    if (!token || (!pendingViews.size && !events.length)) return;
    const body = JSON.stringify({ session, views: [...pendingViews.values()], events });
    pendingViews.clear();
    events = [];
    try {
        fetch(`${API}/analytics/collect`, {
            method: "POST",
            keepalive: body.length < 60000, // keepalive bodies are capped at 64 KB
            credentials: "include",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body,
        }).catch(() => {});
    } catch { /* never let analytics throw */ }
};

/** A new page was entered (route pattern + component name). */
export const startPageView = (route_pattern, component) => {
    if (!started) return;
    if (current) {
        if (current.route_pattern === route_pattern && current.path === window.location.pathname) return;
        pendingViews.set(current.view_id, snapshot(current));
    }
    current = {
        view_id: uuid(),
        route_pattern,
        component: component || null,
        path: window.location.pathname,
        enteredAt: now(),
        visibleSince: document.visibilityState === "visible" ? now() : null,
        activeMs: 0,
        loadMs: null,
        apiCalls: 0,
        apiErrors: 0,
    };
    flush();
};

/** Feature usage: track("export", "party-list"), track("search", "/salesbill"). */
export const track = (type, name, meta) => {
    if (!started) return;
    events.push({ type, name: name == null ? null : String(name).slice(0, 200), meta: meta || null, route_pattern: current?.route_pattern || null, at: new Date().toISOString() });
    if (events.length >= 50) flush();
};

/** Called by the axios client for every finished request. */
export const noteApiCall = (startedAt, ok) => {
    if (!current || !startedAt) return;
    current.apiCalls += 1;
    if (!ok) current.apiErrors += 1;
    if (startedAt - current.enteredAt <= LOAD_WINDOW_MS) {
        current.loadMs = Math.max(current.loadMs || 0, now() - current.enteredAt);
    }
};

/** Replace digits in an endpoint so it groups (e.g. /party/60/pdf → /party/:id/pdf). */
export const endpointPattern = (url) => String(url || "").split("?")[0].replace(/\/\d+(?=\/|$)/g, "/:id");

const onVisibility = () => {
    if (!current) return;
    if (document.visibilityState === "visible") {
        current.visibleSince = now();
    } else {
        current.activeMs = activeMs(current);
        current.visibleSince = null;
        flush();
    }
};

const onError = (message) => track("js_error", String(message || "Unknown error").slice(0, 200));

/** Start once (Routes.js). Safe to call repeatedly. */
export const startAnalytics = () => {
    if (started || typeof window === "undefined") return;
    started = true;
    session = loadSession();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    window.addEventListener("error", (e) => onError(e?.message));
    window.addEventListener("unhandledrejection", (e) => {
        const r = e?.reason;
        // Cancelled requests (AbortController) aren't errors.
        if (r?.name === "CanceledError" || r?.name === "AbortError" || r?.code === "ERR_CANCELED" || r?.statusCode) return;
        onError(r?.message || r);
    });
    timer = setInterval(flush, FLUSH_MS);
};

/** Tests only. */
export const _resetAnalytics = () => {
    started = false; current = null; events = []; pendingViews.clear(); session = null;
    if (timer) clearInterval(timer);
    timer = null;
};
